import { useId, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import { Card } from '../../components/ui/Card';
import { cn } from '../../lib/cn';

export type SidePanelTab = 'preview' | 'assist';

const TABS: { id: SidePanelTab; label: string }[] = [
  { id: 'preview', label: 'Preview' },
  { id: 'assist', label: 'AI assist' },
];

/**
 * The tab a key moves to from `current`, or null for a key the tablist does not handle. Left and
 * Right wrap around, Home and End jump to the ends - the ARIA tabs pattern.
 */
export function tabAfterKey(current: SidePanelTab, key: string): SidePanelTab | null {
  const at = TABS.findIndex((t) => t.id === current);
  switch (key) {
    case 'ArrowRight':
      return TABS[(at + 1) % TABS.length].id;
    case 'ArrowLeft':
      return TABS[(at - 1 + TABS.length) % TABS.length].id;
    case 'Home':
      return TABS[0].id;
    case 'End':
      return TABS[TABS.length - 1].id;
    default:
      return null;
  }
}

/**
 * The composer's right-hand card: a Preview of the post as its platform will show it, and AI assist.
 * Preview is the default. Both panels stay mounted, so switching never loses anything either one
 * is holding.
 */
export function PreviewAssistCard({
  preview,
  assist,
  defaultTab = 'preview',
}: {
  preview: ReactNode;
  assist: ReactNode;
  defaultTab?: SidePanelTab;
}) {
  const [tab, setTab] = useState<SidePanelTab>(defaultTab);
  const baseId = useId();
  const tabRefs = useRef(new Map<SidePanelTab, HTMLButtonElement>());
  const tabId = (t: SidePanelTab) => `${baseId}-${t}-tab`;
  const panelId = (t: SidePanelTab) => `${baseId}-${t}-panel`;

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    // Alt+Left is the browser's Back; modified keys are never the tablist's.
    if (e.altKey || e.ctrlKey || e.metaKey) return;
    const next = tabAfterKey(tab, e.key);
    if (!next) return;
    e.preventDefault();
    setTab(next);
    // Focus goes with the selection: only the selected tab is in the Tab order.
    tabRefs.current.get(next)?.focus();
  };

  return (
    <Card className="h-fit">
      <div
        role="tablist"
        aria-label="Post preview and AI assist"
        onKeyDown={onKeyDown}
        // Wraps rather than overflowing the card at a large browser text size.
        className="flex flex-wrap gap-1 border-b border-line-soft px-3"
      >
        {TABS.map(({ id, label }) => {
          const selected = tab === id;
          return (
            <button
              key={id}
              ref={(el) => {
                if (el) tabRefs.current.set(id, el);
                else tabRefs.current.delete(id);
              }}
              type="button"
              role="tab"
              id={tabId(id)}
              aria-selected={selected}
              aria-controls={panelId(id)}
              tabIndex={selected ? 0 : -1}
              onClick={() => setTab(id)}
              className={cn(
                'focus-ring -mb-px min-h-11 border-b-2 px-3 text-sm font-medium transition-colors',
                selected
                  ? // Forced colours repaint both underlines the same; a system colour survives.
                    'border-action text-ink forced-colors:[border-color:Highlight]'
                  : // …and paint the other one's out, or forced colours (and hover there) mark both.
                    'border-transparent text-ink-muted hover:text-ink forced-colors:[border-color:Canvas]',
              )}
            >
              {label}
            </button>
          );
        })}
      </div>
      {TABS.map(({ id }) => (
        <div
          key={id}
          role="tabpanel"
          id={panelId(id)}
          aria-labelledby={tabId(id)}
          hidden={tab !== id}
          // Reachable by Tab even when a panel holds nothing focusable (a locked post's notice).
          tabIndex={0}
          className="focus-ring rounded-b-xl px-5 py-4"
        >
          {id === 'preview' ? preview : assist}
        </div>
      ))}
    </Card>
  );
}
